// @ts-nocheck
/**
 * @typedef PackProductData
 * @type {object}
 * @property {string} variant_id - Shopify product variant ID, liquid equivalent product.variant.id (ex. 47135596380419)
 * @property {number} quantity - number of item in a pack
 */

/**
 * @typedef MetafieldQueryDefinition
 * @type {object}
 * @property {string} [key]
 * @property {string} [namespace]
 * @property {string} [query]
 */

/** 
 * @typedef MetafieldQuery 
 * @type {Object.<string, MetafieldQueryDefinition>}
*/

import { ThemeEvents } from '@theme/events';
import { StorefrontGQL } from '@fgtheme/utilities';

class SplashPackProducts extends HTMLElement {
    #swiper = null;

    /** @type {PackProductData[]} */
    #packProducts = [];

    /** 
     * @type {{
     *  id: string,
     *  product: any
     * }[]} 
     * */
    #productData = [];

    constructor() {
        super();
    }

    connectedCallback() {
        const variantPicker = document.querySelector('[data-product-grid-content] variant-picker');
        if (variantPicker) {
            variantPicker.addEventListener(ThemeEvents.variantSelected, this.#onVariantChange.bind(this));
        }
        const productsRaw = this.dataset.products ?? '';
        try {
            this.#packProducts = JSON.parse(productsRaw);
        } catch (e) {
            console.error('Error parsing product json', e);
        }
        this.init();
    }

    async init() {
        await this.getProductData();
        this.renderProducts();
        const swiperElement = this.querySelector('.swiper');
        const paginationElement = this.querySelector('.swiper-pagination');
        const navigationNext = this.querySelector('.swiper-button-next');
        const navigationPrev = this.querySelector('.swiper-button-prev');
        this.#swiper = new Swiper(swiperElement, {
            slidesPerView: "auto",
            spaceBetween: 24,
            centeredSlides: true,
            loop: true,
            mousewheel: {
                releaseOnEdges: true,
                forceToAxis: true
            },
            navigation: {
                nextEl: navigationNext,
                prevEl: navigationPrev
            },
            pagination: {
                el: paginationElement,
                renderCustom: (swiper, current, total) => {
                    return `<span class="current">${current}</span> of <span class="total">${total}</span>`
                },
                type: 'custom'
            },
            breakpoints: {
                0: {
                    slidesPerView: 1
                },
                500: {
                    slidesPerView: "auto"
                }
            }
        });
    }

    disconnectedCallback() {
        const variantPicker = document.querySelector('[data-product-grid-content] variant-picker');
        if (variantPicker) {
            variantPicker.removeEventListener(ThemeEvents.variantSelected, this.#onVariantChange)
        }
    }

    /**
     * 
     * @param {*} event 
     */
    async #onVariantChange(event) {
        const detailId = event.detail.resource.id;
        const variantButton = event.srcElement.querySelector(`[data-option-value-id="${detailId}"]`);
        if (!variantButton) return;
        const variantId = variantButton.dataset.variantId;
        const productVariant = await this.getProductVariant(variantId);
        try {
            this.#packProducts = JSON.parse(productVariant.packProducts.value);
            this.#swiper.destroy(true, true);
            this.querySelector('.swiper').remove();
            this.querySelector('.inline-controls').remove();
            this.init();
        } catch (e) {
            console.error(e);
        }
    }

    renderProducts() {
        const containerTpl = this.querySelector('template[data-tpl="container"]');
        const productTpl = this.querySelector('template[data-tpl="product"]');
        const metafieldTpl = this.querySelector('template[data-tpl="metafield"]');
        const metafieldConfigTpl = this.querySelector('template[data-tpl="metaobjects_config"');

        const containerFragment = containerTpl.content.cloneNode(true);
        const carouselContainer = containerFragment.querySelector('[data-area="carousel"]');
        this.#productData.forEach(productVariant => {
            const productFragment = productTpl.content.cloneNode(true);
            const productTitle = productFragment.querySelector('[data-area="title"]');
            if (!productVariant) {
                productTitle.textContent = 'Cannot fetch product data';
            } else {
                productTitle.textContent = productVariant.product.title;

                const productQuantity = productFragment.querySelector('[data-area="quantity"]');
                const bottleQuantity = this.getBottleQuantity(productVariant.id);
                productQuantity.textContent = bottleQuantity > 1 ? `${bottleQuantity} Bottles` : `${bottleQuantity} Bottle`;

                if (productVariant.product.featuredImage) {
                    const productImage = productFragment.querySelector('[data-area="product-image"]');
                    productImage.innerHTML = `<img src=${productVariant.product.featuredImage.url} alt="${productVariant.product.featuredImage.altText}">`;
                }

                if (productVariant.product.metafield_pairsWith && productVariant.product.metafield_pairsWith.value) {
                    const paringsList = productVariant.product.metafield_pairsWith.references.nodes.map(node => node.name.value);
                    const productPairings = productFragment.querySelector('[data-area="pairings"]');
                    productPairings.textContent = paringsList.join(", ");
                } else {
                    const productPairingsContainer = productFragment.querySelector('.pairings');
                    productPairingsContainer.classList.add('hidden');
                }

                if (productVariant.product.metafield_country && productVariant.product.metafield_country.value) {
                    const config = metafieldConfigTpl.content.querySelector('[data-handle="country"]');
                    const metafield_country = metafieldTpl.content.cloneNode(true);
                    metafield_country.querySelector('[data-area="icon"]').innerHTML = `<img src="${config.dataset.image}">`;
                    metafield_country.querySelector('[data-area="label"]').textContent = `${productVariant.product.metafield_country.value}`;
                    productFragment.querySelector('[data-area="pills"]').appendChild(metafield_country);
                }

                if (productVariant.product.metafield_varietal && productVariant.product.metafield_varietal.value) {
                    const config = metafieldConfigTpl.content.querySelector('[data-handle="varietal"]');
                    const metafield_varietal = metafieldTpl.content.cloneNode(true);
                    metafield_varietal.querySelector('[data-area="icon"]').innerHTML = `<img src="${config.dataset.image}">`;
                    metafield_varietal.querySelector('[data-area="label"]').textContent = `${productVariant.product.metafield_varietal.value}`;
                    productFragment.querySelector('[data-area="pills"]').appendChild(metafield_varietal);
                }

                if (productVariant.product.metafield_sweetness && productVariant.product.metafield_sweetness.value) {
                    const config = metafieldConfigTpl.content.querySelector('[data-handle="sweetness"]');
                    const metafield_sweetness = metafieldTpl.content.cloneNode(true);
                    metafield_sweetness.querySelector('[data-area="icon"]').innerHTML = `<img src="${config.dataset.image}">`;
                    metafield_sweetness.querySelector('[data-area="label"]').textContent = `${productVariant.product.metafield_sweetness.reference.name.value}`;
                    productFragment.querySelector('[data-area="pills"]').appendChild(metafield_sweetness);
                }

                if (productVariant.product.metafield_abv && productVariant.product.metafield_abv.value) {
                    const config = metafieldConfigTpl.content.querySelector('[data-handle="abv"]');
                    const metafield_abv = metafieldTpl.content.cloneNode(true);
                    metafield_abv.querySelector('[data-area="icon"]').innerHTML = `<img src="${config.dataset.image}">`;
                    metafield_abv.querySelector('[data-area="label"]').textContent = `${productVariant.product.metafield_abv.value}% ABV`;
                    productFragment.querySelector('[data-area="pills"]').appendChild(metafield_abv);
                }
            }

            carouselContainer.appendChild(productFragment);
        });
        this.appendChild(containerFragment);
    }

    getBottleQuantity(variantId) {
        const found = this.#packProducts.find(product => product.variant_id === variantId);
        if (!found) return null;
        return found.quantity;
    }

    /**
     * 
     * @returns {MetafieldQuery}
     */
    defineMetafields() {
        /**
         * @type {MetafieldQuery}
         */
        const fields = {
            'country': { key: 'country', namespace: 'fgw' },
            'sweetness': {
                query: `
                    metafield(namespace: "fgw", key: "sweetness_scale") {
                        type
                        value
                        reference {
                            ... on Metaobject {
                                id
                                handle
                                name: field(key: "title") {
                                    value
                                }
                            }
                        }
                    }
                `
            },
            'pairsWith': {
                query: `
                    metafield(namespace: "fgw", key: "food_pairing") {
                        type
                        value
                        references(first: 10) {
                            nodes {
                                ... on Metaobject {
                                    id
                                    handle
                                    name: field(key:"title") {
                                        value
                                    }
                                }
                            }
                        }
                    }
                `
            },
            'abv': { key: 'abv', namespace: 'fgw' },
            'varietal': { key: 'marketing_varietal', namespace: 'fgw' },
        }

        return fields;
    }

    buildMetafieldsQuery() {
        const metafields = this.defineMetafields();
        const queries = [];
        for (const [prop, value] of Object.entries(metafields)) {
            if (!value.query) {
                queries.push(`
                    metafield_${prop}: metafield(namespace: "${value.namespace}", key: "${value.key}") {
                        type
                        value
                    }
                `.trim()
                );
            } else {
                queries.push(`
                    metafield_${prop}: ${value.query}
                `
                );
            }
        }
        return queries.join("\n");
    }

    async getProductData() {
        const metafieldsQuery = this.buildMetafieldsQuery();
        const query = {
            query: `
                query GetNodes($ids: [ID!]!) {
                    nodes(ids: $ids) {
                        ... on ProductVariant {
                            id
                            product {
                                id
                                title
                                handle
                                featuredImage {
                                    altText
                                    url(transform: {maxWidth: 600, maxHeight: 600})
                                }
                                ${metafieldsQuery}
                            }
                        }
                    }
                }
            `,
            variables: {
                ids: this.#packProducts.map(data => data.variant_id)
            }
        };
        const response = await StorefrontGQL(query);
        const result = await response.json();
        if (!result || !result.data || !result.data.nodes) {
            console.error('Product data fetch error', result);
            this.#productData = [];
            return;
        }
        this.#productData = result.data.nodes;
    }

    /**
     * 
     * @param {string} id 
     */
    async getProductVariant(id) {
        const query = {
            query: `
                query GetProductVariant($id: ID!) {
                    node(id: $id) {
                        ... on ProductVariant {
                            id
                            packProducts: metafield(namespace: "fgw", key: "pack_products") {
                                value
                            }
                        }
                    }
                }
            `,
            variables: {
                id: `gid://shopify/ProductVariant/${id}`
            }
        }

        const response = await StorefrontGQL(query);
        const result = await response.json();
        return result.data.node;
    }
}

if (!customElements.get('splash-pack-products')) {
    customElements.define('splash-pack-products', SplashPackProducts);
}